/*
 * Maps each entity type shown on the Demo Environment Management page to the
 * underlying csq* helper functions, so the page component can run list /
 * create / delete uniformly without a big switch statement per action.
 *
 * Also exports composite `deleteAllEntityTypes` / `createAllEntityTypes`
 * (entity type "all"), mirroring sales-demo-env-setup's
 * `src/delete-all.js` / `src/create-all.js`.
 */

import * as mappings from './csqMappings';
import * as zonings from './csqZonings';
import * as segments from './csqSegments';
import * as properties from './csqProperties';
import * as charts from './csqCharts';
import * as dashboards from './csqDashboards';
import { defaultZoningConfig } from './templates/zoningTemplates';

// Datasource-level entity types cleared/created per target datasource; project-
// level entity types cleared/created once, on the master project (mirrors
// delete-all.js/create-all.js's REPLICA_PROJECTS vs CHART_PROJECT split).
const DATASOURCE_ENTITY_TYPES = ['mappings', 'zonings', 'segments', 'properties'];
const PROJECT_ENTITY_TYPES = ['charts', 'dashboards'];

export const ENTITY_HANDLERS = {
  mappings: {
    level: 'datasource',
    list: (client, id) => mappings.listMappings(client, id),
    create: (client, id, config) => mappings.createMappingAndPageGroup(client, id, config),
    delete: (client, id, itemId) => mappings.deleteMapping(client, id, itemId),
  },
  zonings: {
    level: 'datasource',
    list: (client, id) => zonings.listZonings(client, id),
    create: async (client, id, config, log) => {
      const { pageId, pageUrl, pageName, pageMappingName, pageTarget, ...zoningOverrides } = config;
      let resolvedPageId = pageId;
      if (!resolvedPageId) {
        log('Resolving page...');
        const page = pageUrl
          ? await mappings.resolvePageByUrl(client, id, pageUrl)
          : await mappings.resolvePage(client, id, { pageName, mappingName: pageMappingName, target: pageTarget, log });
        resolvedPageId = page.id;
        log(`  resolved page "${page.name}" (id ${page.id}).`);
      }
      const zoningConfig = defaultZoningConfig({ ...zoningOverrides, pageId: resolvedPageId, log });
      return zonings.createZoning(client, id, zoningConfig);
    },
    delete: (client, id, itemId) => zonings.deleteZoning(client, id, itemId),
  },
  segments: {
    level: 'datasource',
    list: (client, id) => segments.listSegments(client, id),
    create: (client, id, config) => segments.createSegment(client, id, config),
    delete: (client, id, itemId) => segments.deleteSegment(client, id, itemId),
  },
  properties: {
    level: 'datasource',
    list: (client, id) => properties.listProperties(client, id),
    create: (client, id, config) => properties.createProperty(client, id, config),
    delete: (client, id, itemId) => properties.deleteProperty(client, id, itemId),
  },
  charts: {
    level: 'project',
    list: (client, id) => charts.listCharts(client, id),
    defaultCreateConfig: () => ({ name: 'Top devices', dashboardName: 'New dashboard' }),
    create: (client, id, config, log) => charts.createChartWithVersion(client, id, { ...config, log }),
    delete: (client, id, itemId) => charts.deleteChart(client, id, itemId),
  },
  dashboards: {
    level: 'project',
    list: (client, id) => dashboards.listDashboards(client, id),
    create: (client, id, config) => dashboards.createDashboard(client, id, config.name),
    delete: (client, id, itemId) => dashboards.deleteDashboard(client, id, itemId),
  },
};

/**
 * Deletes every item currently listed for an entity type/datasource (or
 * project), one at a time, tolerating individual failures so the loop
 * keeps going (mirrors delete-all.js's `clearEntity`).
 *
 * @returns {Promise<{ found: number, deleted: number, failed: number, failures: Array }>}
 */
export async function deleteAllItems(client, handler, id, items, log) {
  const failures = [];
  let deleted = 0;
  for (const item of items) {
    try {
      await handler.delete(client, id, item.id);
      deleted += 1;
      log(`  ${item.id} (${item.name || '(unnamed)'}) deleted OK`);
    } catch (err) {
      failures.push({ item, error: err.message });
      log(`  ${item.id} (${item.name || '(unnamed)'}) FAILED: ${err.message}`);
    }
  }
  return { found: items.length, deleted, failed: failures.length, failures };
}

/**
 * Guards against ever targeting the source datasource with a
 * create/delete action (composite or single-entity-type): it is the
 * mappings source of truth (create copies FROM it, never writes to it) and
 * must never appear in the target datasource list (mirrors delete-all.js's
 * `assertMainProjectExcluded`).
 *
 * @param {Array} targetDatasourceIds
 * @param {number|string} sourceDatasourceId - The currently selected source datasource id.
 */
export function assertSourceDatasourceExcluded(targetDatasourceIds, sourceDatasourceId) {
  if (targetDatasourceIds.map(String).includes(String(sourceDatasourceId))) {
    throw new Error(
      `Refusing to run: the source datasource (${sourceDatasourceId}) is in the target datasource list ` +
        `(${targetDatasourceIds.join(', ')}). The source datasource must never be cleared or targeted — ` +
        'remove it from Target datasources.',
    );
  }
}

/**
 * Lists custom properties across both the "event" and "user" levels, combined
 * into a single array (mirrors delete-all.js's `listAllProperties`).
 */
async function listAllDatasourceProperties(client, datasourceId) {
  const [eventItems, userItems] = await Promise.all([
    properties.listProperties(client, datasourceId, { level: 'event' }),
    properties.listProperties(client, datasourceId, { level: 'user' }),
  ]);
  return [...eventItems, ...userItems];
}

/**
 * Lists every item of one entity type at one scope (datasource or project id),
 * tolerating listing failures by logging a warning and returning `null` instead
 * of throwing (shared by `deleteAllEntityTypes`'s clear step and
 * `listAllEntityTypes`'s read-only preview step).
 */
async function listEntityItemsSafe(client, scopeId, entityType, log, warnings) {
  try {
    const items = entityType === 'properties'
      ? await listAllDatasourceProperties(client, scopeId)
      : await ENTITY_HANDLERS[entityType].list(client, scopeId);
    log(`[${scopeId}] ${entityType}: found ${items.length}`);
    return items;
  } catch (err) {
    warnings.push({ scopeId, entityType, error: err.message });
    log(`[${scopeId}] ${entityType}: WARNING — could not list (${err.message}). Skipping.`);
    return null;
  }
}

/**
 * Composite "delete-all": clears mappings + zonings + segments + custom
 * properties on every target datasource, plus charts + dashboards on the
 * master project. Mirrors sales-demo-env-setup's `src/delete-all.js`.
 *
 * `sourceDatasourceId` (the currently selected source datasource) is only used to
 * guard against it accidentally appearing in `targetDatasourceIds` — it is
 * never itself listed or cleared.
 *
 * Listing failures (e.g. a 404 because a datasource doesn't have that feature
 * enabled) are logged as warnings and do NOT stop the run. Only delete
 * failures are treated as hard failures.
 *
 * Pass `dryRun: true` to only list what would be deleted (no delete calls) —
 * used to build the page's preview/confirm step.
 *
 * @returns {Promise<{ found: number, deleted: number, failed: number, failures: Array, warnings: Array }>}
 */
export async function deleteAllEntityTypes(client, { targetDatasourceIds, projectId, sourceDatasourceId, log, dryRun = false }) {
  assertSourceDatasourceExcluded(targetDatasourceIds, sourceDatasourceId);

  const warnings = [];
  const failures = [];
  const successByType = {};
  let found = 0;
  let deleted = 0;

  const clearEntity = async (scopeId, entityType) => {
    const items = await listEntityItemsSafe(client, scopeId, entityType, log, warnings);
    if (items === null) return;

    found += items.length;
    if (items.length === 0 || dryRun) return;

    const result = await deleteAllItems(client, ENTITY_HANDLERS[entityType], scopeId, items, (line) => log(`  ${line}`));
    deleted += result.deleted;
    successByType[entityType] = (successByType[entityType] || 0) + result.deleted;
    result.failures.forEach((f) => failures.push({ scopeId, entityType, ...f }));
  };

  for (const datasourceId of targetDatasourceIds) {
    for (const entityType of DATASOURCE_ENTITY_TYPES) {
      await clearEntity(datasourceId, entityType);
    }
  }
  for (const entityType of PROJECT_ENTITY_TYPES) {
    await clearEntity(projectId, entityType);
  }

  return { found, deleted, failed: failures.length, failures, warnings, successByType };
}

/**
 * Read-only counterpart to `deleteAllEntityTypes` — lists every entity type
 * (mappings/zonings/segments/properties per datasource id, charts/dashboards
 * on the project) without deleting anything. Used to preview what a Resync's
 * recreate step would pull from Source — pass a SOURCE-authenticated `client`
 * along with the SOURCE datasource id(s) and SOURCE project id, matching
 * exactly what `createAllEntityTypes`'s `sourceClient` param reads, so the
 * preview reflects what Confirm will actually see (including a 403 if the
 * client's account has no access to the source project).
 *
 * @returns {Promise<{ found: number, warnings: Array }>}
 */
export async function listAllEntityTypes(client, { datasourceIds, projectId, log }) {
  const warnings = [];
  let found = 0;

  for (const datasourceId of datasourceIds) {
    for (const entityType of DATASOURCE_ENTITY_TYPES) {
      const items = await listEntityItemsSafe(client, datasourceId, entityType, log, warnings);
      if (items) found += items.length;
    }
  }
  for (const entityType of PROJECT_ENTITY_TYPES) {
    const items = await listEntityItemsSafe(client, projectId, entityType, log, warnings);
    if (items) found += items.length;
  }

  return { found, warnings };
}

/**
 * Composite "create-all": replicates every mapping (with its pages/page-groups), zoning,
 * segment, and custom property from the source datasource onto each target datasource, then
 * recreates every dashboard (widget groups + widgets) and replicates every chart from the
 * source project onto the master project. Mirrors sales-demo-env-setup's `src/create-all.js`,
 * except zonings/charts/dashboards/segments/properties/mappings are now a full replica of the
 * source's items rather than one fixed default per entity type.
 *
 * Dashboards are recreated before charts: a chart is grouped under whichever source dashboard it
 * was attached to (via the legacy blocks model — see the `chartIdToDashboardName` lookup below)
 * and then attached to the matching, already-recreated target dashboard (by name), instead of
 * every chart creating its own brand new dashboard.
 *
 * Chart creation is best-effort (a chart's snapshot query may not resolve the same way against
 * the target's data) — failures there are logged as warnings and do not stop the run. Zoning
 * creation is also best-effort per item (e.g. the resolved page may have no recorded traffic yet
 * on the target so a screenshot can't be captured), but if EVERY zoning for a given target
 * datasource fails (zero created), that datasource's zoning issues are escalated to hard
 * failures instead — see the `zoningsCreatedForDatasource` check below.
 * Mappings/zonings/charts/segments/properties that already exist on the target (by name)
 * are skipped rather than duplicated. Mapping/segment/property/dashboard failures are
 * hard failures.
 *
 * `sourceClient` and `targetClient` are independent — the source and target
 * datasources/projects can live under different ContentSquare accounts, so
 * every read against the source uses `sourceClient` and every read/write
 * against the target uses `targetClient` (never mixed, or a call scoped to
 * one side fails with a 403 when the other side's account lacks access to it).
 *
 * @returns {Promise<{ failures: Array, warnings: Array }>}
 */
export async function createAllEntityTypes(sourceClient, targetClient, { sourceDatasourceId, targetDatasourceIds, projectId, sourceProjectId, log }) {
  assertSourceDatasourceExcluded(targetDatasourceIds, sourceDatasourceId);

  const warnings = [];
  const failures = [];
  const successByType = {};
  const bumpSuccess = (entityType, count = 1) => { successByType[entityType] = (successByType[entityType] || 0) + count; };

  log(`Replicating mappings from source datasource ${sourceDatasourceId} into targets ${targetDatasourceIds.join(', ')}...`);
  const { operations: mappingOperations, collisions: mappingCollisions } = await mappings.buildMappingsCreateFromSourceOperations({
    sourceClient,
    targetClient,
    sourceDatasourceId,
    targetDatasourceIds,
    log: (line) => log(`  ${line}`),
  });
  if (mappingCollisions.length > 0) {
    log(`  ${mappingCollisions.length} name collision(s) skipped (already exist on target): ${mappingCollisions.join(', ')}`);
  }
  if (mappingOperations.length === 0) {
    warnings.push({ scopeId: sourceDatasourceId, entityType: 'mappings', error: 'source datasource has no mappings to replicate' });
    log(`  WARNING: source datasource ${sourceDatasourceId} has no mappings to replicate.`);
  } else {
    const mappingResult = await mappings.runMappingsCreateFromSourceOperations(targetClient, mappingOperations, (line) => log(`  ${line}`));
    log(`  Created ${mappingResult.successCount}/${mappingOperations.length} mapping(s).`);
    bumpSuccess('mappings', mappingResult.successCount);
    mappingResult.failures.forEach((f) => failures.push({ scopeId: f.targetDatasourceId, entityType: 'mappings', error: `"${f.mappingData.name}": ${f.error}` }));
  }

  log(`Listing segments from source datasource ${sourceDatasourceId}...`);
  let sourceSegments = [];
  try {
    sourceSegments = await segments.listSegmentsWithQuery(sourceClient, sourceDatasourceId);
    log(`  found ${sourceSegments.length} source segment(s).`);
  } catch (err) {
    warnings.push({ scopeId: sourceDatasourceId, entityType: 'segments', error: err.message });
    log(`  WARNING: could not list source segments (${err.message}). Skipping segment replication.`);
  }

  log(`Listing custom properties from source datasource ${sourceDatasourceId}...`);
  let sourceProperties = [];
  try {
    sourceProperties = await properties.listAllPropertiesWithFormula(sourceClient, sourceDatasourceId);
    log(`  found ${sourceProperties.length} source custom propert${sourceProperties.length === 1 ? 'y' : 'ies'}.`);
  } catch (err) {
    warnings.push({ scopeId: sourceDatasourceId, entityType: 'properties', error: err.message });
    log(`  WARNING: could not list source custom properties (${err.message}). Skipping property replication.`);
  }

  log(`Listing zonings from source datasource ${sourceDatasourceId}...`);
  let sourceZonings = [];
  let sourcePageById = new Map();
  try {
    sourceZonings = await zonings.listZoningsWithDetails(sourceClient, sourceDatasourceId);
    log(`  found ${sourceZonings.length} source zoning(s).`);
    const sourcePages = await mappings.listPages(sourceClient, sourceDatasourceId, { take: 1000 });
    sourcePageById = new Map(sourcePages.map((page) => [page.id, page]));
  } catch (err) {
    warnings.push({ scopeId: sourceDatasourceId, entityType: 'zonings', error: err.message });
    log(`  WARNING: could not list source zonings (${err.message}). Skipping zoning replication.`);
  }

  for (const datasourceId of targetDatasourceIds) {
    log(`[${datasourceId}] Replicating zoning(s) from source...`);
    if (sourceZonings.length === 0) {
      log(`  skipped: no zonings found on source datasource ${sourceDatasourceId}.`);
    } else {
      let existingNames = new Set();
      try {
        existingNames = new Set((await zonings.listZonings(targetClient, datasourceId)).map((z) => z.name));
      } catch (err) {
        warnings.push({ scopeId: datasourceId, entityType: 'zonings', error: err.message });
        log(`  WARNING: could not list existing target zonings (${err.message}).`);
      }
      let zoningsCreatedForDatasource = 0;
      const zoningIssues = [];
      for (const zoning of sourceZonings) {
        if (existingNames.has(zoning.name)) {
          log(`  skipped "${zoning.name}": already exists on target.`);
          continue;
        }
        const sourcePage = sourcePageById.get(zoning.pageId);
        if (!sourcePage) {
          zoningIssues.push(`source zoning "${zoning.name}" has no resolvable page (pageId ${zoning.pageId})`);
          log(`  WARNING: skipped "${zoning.name}": source page (pageId ${zoning.pageId}) could not be resolved.`);
          continue;
        }
        try {
          const created = await ENTITY_HANDLERS.zonings.create(
            targetClient,
            datasourceId,
            { name: zoning.name, pageName: sourcePage.name, pageMappingName: sourcePage.mappingName, pageTarget: sourcePage.target, device: zoning.device, width: zoning.width, height: zoning.height, userAgent: zoning.userAgent },
            (line) => log(`  ${line}`),
          );
          log(`  zoning ${created.zoning.id} (${created.zoning.name}) created.`);
          bumpSuccess('zonings');
          zoningsCreatedForDatasource += 1;
        } catch (err) {
          zoningIssues.push(`"${zoning.name}": ${err.message}`);
          log(`  WARNING: zoning "${zoning.name}" skipped for datasource ${datasourceId}: ${err.message}`);
        }
      }
      if (zoningIssues.length > 0) {
        // Zoning creation is best-effort per item — but if NONE of them could be created for
        // this datasource, that's not a partial success to shrug off as warnings; escalate to
        // hard failures so the run summary reflects that this datasource ended up with zero
        // zonings, instead of reading as clean.
        const bucket = zoningsCreatedForDatasource === 0 ? failures : warnings;
        zoningIssues.forEach((error) => bucket.push({ scopeId: datasourceId, entityType: 'zonings', error }));
      }
    }

    log(`[${datasourceId}] Replicating segment(s) from source...`);
    if (sourceSegments.length === 0) {
      log(`  skipped: no segments found on source datasource ${sourceDatasourceId}.`);
    } else {
      let existingNames = new Set();
      try {
        existingNames = new Set((await segments.listSegments(targetClient, datasourceId, { take: 1000 })).map((s) => s.name));
      } catch (err) {
        warnings.push({ scopeId: datasourceId, entityType: 'segments', error: err.message });
        log(`  WARNING: could not list existing target segments (${err.message}).`);
      }
      for (const segment of sourceSegments) {
        if (existingNames.has(segment.name)) {
          log(`  skipped "${segment.name}": already exists on target.`);
          continue;
        }
        try {
          const created = await segments.createSegment(targetClient, datasourceId, { name: segment.name, query: segment.query });
          log(`  segment ${created.id} (${created.name}) created.`);
          bumpSuccess('segments');
        } catch (err) {
          failures.push({ scopeId: datasourceId, entityType: 'segments', error: err.message });
          log(`  FAILED segment "${segment.name}": ${err.message}`);
        }
      }
    }

    log(`[${datasourceId}] Replicating custom propert${sourceProperties.length === 1 ? 'y' : 'ies'} from source...`);
    if (sourceProperties.length === 0) {
      log(`  skipped: no custom properties found on source datasource ${sourceDatasourceId}.`);
    } else {
      let existingNames = new Set();
      try {
        existingNames = new Set((await properties.listAllPropertiesWithFormula(targetClient, datasourceId)).map((p) => p.name));
      } catch (err) {
        warnings.push({ scopeId: datasourceId, entityType: 'properties', error: err.message });
        log(`  WARNING: could not list existing target custom properties (${err.message}).`);
      }
      for (const property of sourceProperties) {
        if (existingNames.has(property.name)) {
          log(`  skipped "${property.name}": already exists on target.`);
          continue;
        }
        try {
          const created = await properties.createProperty(targetClient, datasourceId, {
            name: property.name,
            level: property.level,
            formula: property.formula,
            notes: property.notes,
          });
          log(`  property ${created.id} (${created.name}) created.`);
          bumpSuccess('properties');
        } catch (err) {
          failures.push({ scopeId: datasourceId, entityType: 'properties', error: err.message });
          log(`  FAILED property "${property.name}": ${err.message}`);
        }
      }
    }
  }

  // Dashboards are recreated BEFORE charts below: charts have no dashboard info of their own (see
  // chartIdToDashboardName below), so replicating charts needs the matching target dashboard (by
  // source dashboard name) to already exist, instead of creating a brand new one per chart.
  log(`[${projectId}] Recreating dashboards from source project ${sourceProjectId}...`);
  const targetDashboardIdByName = new Map();
  try {
    const sourceDashboards = await dashboards.listDashboards(sourceClient, sourceProjectId);
    if (sourceDashboards.length === 0) {
      warnings.push({ scopeId: sourceProjectId, entityType: 'dashboards', error: 'source project has no dashboards to recreate' });
      log(`  WARNING: source project ${sourceProjectId} has no dashboards to recreate.`);
    } else {
      for (const sourceDashboard of sourceDashboards) {
        log(`  "${sourceDashboard.name}"...`);
        try {
          // A v5 (chart-based) source dashboard has no widget-groups/widgets of its own — those
          // v2/v3 endpoints 500 for it, since that model only exists for v4 dashboards. Recreate
          // it via v5 too, and skip the widget-group/widget copy: its actual content (chart
          // attachments) is populated during chart replication below, which matches charts back to
          // this dashboard by name via `targetDashboardIdByName`.
          const isChartDashboard = sourceDashboard.version === 'v5';
          const targetDashboard = isChartDashboard
            ? await dashboards.createDashboardForChart(targetClient, projectId, sourceDashboard.name)
            : await dashboards.createDashboard(targetClient, projectId, sourceDashboard.name);
          const targetDashboardId = targetDashboard && targetDashboard.id;
          if (!targetDashboardId) {
            throw new Error(`Unexpected create-dashboard response: ${JSON.stringify(targetDashboard)}`);
          }
          if (!targetDashboardIdByName.has(sourceDashboard.name)) targetDashboardIdByName.set(sourceDashboard.name, targetDashboardId);

          if (isChartDashboard) {
            log(`    created dashboard ${targetDashboardId} (chart-based — content attached during chart replication).`);
            bumpSuccess('dashboards');
            continue;
          }

          const { groupCount, widgetCount } = await dashboards.copyDashboardContent(sourceClient, targetClient, {
            sourceProjectId,
            sourceDashboardId: sourceDashboard.id,
            targetProjectId: projectId,
            targetDashboardId,
            log: (line) => log(`    ${line}`),
          });
          log(`    created dashboard ${targetDashboardId} (${groupCount} group(s), ${widgetCount} widget(s)).`);
          bumpSuccess('dashboards');
        } catch (err) {
          failures.push({ scopeId: projectId, entityType: 'dashboards', error: `"${sourceDashboard.name}": ${err.message}` });
          log(`  FAILED dashboard "${sourceDashboard.name}": ${err.message}`);
        }
      }
    }
  } catch (err) {
    warnings.push({ scopeId: sourceProjectId, entityType: 'dashboards', error: err.message });
    log(`  WARNING: could not list source dashboards (${err.message}). Skipping dashboard recreation.`);
  }

  log(`[${projectId}] Replicating charts from source project ${sourceProjectId}...`);
  try {
    const sourceCharts = await charts.listChartsWithDetails(sourceClient, sourceProjectId);
    if (sourceCharts.length === 0) {
      warnings.push({ scopeId: sourceProjectId, entityType: 'charts', error: 'source project has no charts to replicate' });
      log(`  WARNING: source project ${sourceProjectId} has no charts to replicate.`);
    } else {
      log(`  found ${sourceCharts.length} source chart(s).`);

      // A chart's own listing has no dashboard info, so figure out which source dashboard(s)
      // group these charts together (via the legacy blocks model) so each chart can be attached to
      // the matching (already recreated above) target dashboard, instead of a brand new one.
      const chartIdToDashboardName = new Map();
      try {
        const sourceDashboards = await dashboards.listDashboards(sourceClient, sourceProjectId);
        for (const sourceDashboard of sourceDashboards) {
          try {
            const blocks = await dashboards.listDashboardBlocks(sourceClient, sourceProjectId, sourceDashboard.id);
            blocks.forEach((block) => {
              if (block.chartId) chartIdToDashboardName.set(block.chartId, sourceDashboard.name);
            });
          } catch {
            // best-effort — a dashboard with no legacy blocks (e.g. widget-group-only) may error here; skip it.
          }
        }
      } catch (err) {
        log(`  WARNING: could not determine source chart/dashboard grouping (${err.message}) — charts will use a single fallback dashboard.`);
      }

      let existingNames = new Set();
      try {
        existingNames = new Set((await charts.listCharts(targetClient, projectId)).map((c) => c.name));
      } catch (err) {
        warnings.push({ scopeId: projectId, entityType: 'charts', error: err.message });
        log(`  WARNING: could not list existing target charts (${err.message}).`);
      }

      // Charts whose source dashboard couldn't be matched/recreated above fall back to sharing a
      // single "New dashboard", instead of each one creating its own.
      const FALLBACK_DASHBOARD_NAME = 'New dashboard';
      for (const sourceChart of sourceCharts) {
        if (existingNames.has(sourceChart.name)) {
          log(`  skipped "${sourceChart.name}": already exists on target.`);
          continue;
        }
        const dashboardName = chartIdToDashboardName.get(sourceChart.id) || FALLBACK_DASHBOARD_NAME;
        const targetDashboardId = targetDashboardIdByName.get(dashboardName);
        try {
          const created = await ENTITY_HANDLERS.charts.create(
            targetClient,
            projectId,
            targetDashboardId
              ? { snapshotData: sourceChart.snapshotData, dashboardId: targetDashboardId }
              : { snapshotData: sourceChart.snapshotData, dashboardName },
            (line) => log(`  ${line}`),
          );
          if (!targetDashboardId && created.dashboardId) targetDashboardIdByName.set(dashboardName, created.dashboardId);
          log(`  chart ${created.chartId} (${sourceChart.name}) created on dashboard "${dashboardName}".`);
          bumpSuccess('charts');
        } catch (err) {
          // Chart creation is best-effort (legacy v1 charts API — a source chart's snapshot
          // query may not resolve the same way against the target's data), so treat failures
          // as warnings, not hard failures, same as zonings above (per-item, not per-datasource).
          warnings.push({ scopeId: projectId, entityType: 'charts', error: `"${sourceChart.name}": ${err.message}` });
          log(`  WARNING: chart "${sourceChart.name}" skipped: ${err.message}`);
        }
      }
    }
  } catch (err) {
    warnings.push({ scopeId: sourceProjectId, entityType: 'charts', error: err.message });
    log(`  WARNING: could not list source charts (${err.message}). Skipping chart replication.`);
  }

  return { failures, warnings, successByType };
}
